/**
 * The Characteristics & Properties section of the Contentful product pages: the entry's
 * `characteristicsProperties` as cards, then its `characteristicsPropertiesBlurb`, then (on
 * sub-category pages) the grade table built from `gradeTableItem`.
 *
 * - **From `lg` up** the cards are a `components/Careers/IgniteYourCareer/IgniteYourCareer.tsx`
 *   accordion with one shared image (`firstImageOnly`), followed by the blurb and the table on a
 *   split background (60/40 when there is a table, otherwise a single colour).
 * - **Below `lg`** the shared mobile image, each card as a checked heading and description, the
 *   blurb, then one block per grade.
 *
 * Rendered by `components/Contentful/Product.tsx` and `SubProduct.tsx`, with `staticContent` from
 * `content/ryerson/products/productStaticContent.ts` or `subCategoryStaticContent.ts` →
 * `CharacteristicsStaticContent`. The table shows only when both `staticContent.gradeTableHeadings`
 * (sub-category copy only) and `dynamicContent.gradeTableItems` are set.
 *
 * The entry must have at least one `characteristicsProperties` item: both layouts read the first
 * card's image without a guard.
 */
import React from "react";
import {
	IgniteYourCareerContent,
	IgniteYourCareerCard,
} from "@components/Careers/IgniteYourCareer/IgniteYourCareer";
import IgniteYourCareer from "@components/Careers/IgniteYourCareer/IgniteYourCareer";
import { Media } from "@ryerson/frontend.next";
import { ContentSection } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { css } from "@emotion/react";
import { Icon } from "@ryerson/frontend.assets";
import styled from "@emotion/styled";
import { ContentfulCharacteristicsProperties, GradeTableItem } from "./Types/Types";
import Spacer from "@components/Shared/Spacer/Spacer";
/** The grade table's three column headings, one language. */
export type DataTableEntry = {
	grade: string;
	alloyingElement: string;
	characteristics: string;
};

/** One language's fixed copy and images for the section. */
export type StaticCharacteristicsContent = {
	/** Present in the sub-category copy only; without it no grade table is shown. */
	gradeTableHeadings?: DataTableEntry;
	/**
	 * The two title words. Callers join them with a space for the section title and anchor id, and
	 * the desktop accordion places each with its own parallax offsets.
	 */
	title1: string;
	title2: string;
	/** Link text in the page's `SubNavigation` bar. */
	navLabel: string;
	/** Site-relative image shared by every card from `lg` up. */
	desktopImage: string;
	/** Site-relative image shown once above the cards below `lg`. */
	mobileImage: string;
};

/** The section's fields from the Contentful entry. */
export type DynamicCharacteristicsContent = {
	/** The entry's `characteristicsProperties`. At least one is required (see the file header). */
	characteristics: ContentfulCharacteristicsProperties[];
	/** The entry's `characteristicsPropertiesBlurb`; not shown when empty. */
	blurb?: string | null;
	/** The entry's `gradeTableItem` rows. Passed by `SubProduct.tsx` only. */
	gradeTableItems?: GradeTableItem[] | null;
};

/** Not used by this component or imported anywhere. */
export type CharacteristicsContent = {
	characteristics: IgniteYourCareerContent;
	description: string;
	tableHeadings?: DataTableEntry;
	tableData?: DataTableEntry[];
};

/** Props of the default export. */
export type CharacteristicsProps = {
	staticContent: StaticCharacteristicsContent;
	dynamicContent: DynamicCharacteristicsContent;
};

const TableRow = styled.div`
	display: block;
	width: 100%;
`;

const TableCell = styled.div`
	display: inline-block;
	vertical-align: top;
	padding: 20px;
	box-sizing: border-box;
`;

const Divider = styled.hr`
	display: block;
	margin: 0;
	opacity: 0.1;
	width: calc(100% - 60px);
	margin-left: 20px;
`;

const MobileIcon = styled.div`
	display: inline-block;
	width: 18px;
	height: 18px;
	vertical-align: top;
	margin-right: 13px;
`;

const MobileHeading = styled.div`
	display: inline-block;
	width: calc(100% - 31px);
	vertical-align: top;
	padding-right: 20px;
	box-sizing: border-box;
`;

 

/** The Characteristics & Properties section; see the file header. */
const CharacteristicsComponent: React.FC<CharacteristicsProps> = ({
	staticContent,
	dynamicContent,
}) => {
	const { theme } = useTheme();
	let colorsSplit;
	// Every card gets the same two static images; Contentful supplies only titles and text.
	let cards: IgniteYourCareerCard[] = [];

	for (let i = 0; i < dynamicContent.characteristics.length; i++) {
		cards.push({
			title: dynamicContent.characteristics[i].title,
			description: dynamicContent.characteristics[i].description,
			mobileImage: staticContent.mobileImage,
			desktopImage: staticContent.desktopImage,
		});
	}
	if (staticContent.gradeTableHeadings && dynamicContent.gradeTableItems) {
		colorsSplit = [
			{
				color: theme.colors.tertiary.background,
				percentage: "60%",
			},
			{
				color: theme.colors.secondary.background,
				percentage: "40%",
			},
		];
	} else {
		colorsSplit = [
			{
				color: theme.colors.tertiary.background,
				percentage: "100%",
			},
		];
	}

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<IgniteYourCareer
					content={{
						title1: staticContent.title1,
						title2: staticContent.title2,
						navLabel: staticContent.navLabel,
						cards: cards,
					}}
					type="tertiary"
					firstImageOnly={true}
					title1Left="-305px"
					title2Left="-145px"
					title1LeftSmaller="-130px"
					title2LeftSmaller="-35px"
					title1Parallax={["-85px", "-10px"]}
					title2Parallax={["-75px", "-145px"]}
					title1ParallaxSmaller={["-65px", "10px"]}
					title2ParallaxSmaller={["-35px", "-110px"]}
				/>

				<ContentSection
					type="split"
					vPadding="98px"
					split={{
						splitDirection: "vertical",
						splitColors: colorsSplit,
					}}
				>
					{dynamicContent.blurb && (
						<Typography
							variant="h4"
							css={css`
								letter-spacing: -1px;
								display: block;
								max-width: 760px;
								margin-bottom: 54px;
							`}
							type="tertiary"
							color={theme.colors.primary.lighterGray}
						>
							{dynamicContent.blurb}
						</Typography>
					)}

					{staticContent.gradeTableHeadings && dynamicContent.gradeTableItems && (
						<>
							<TableRow
								css={css`
									background-color: ${theme.colors.secondary.background};
									height: 61px;
									border-radius: 2px 2px 0 0;
								`}
							>
								<TableCell
									css={css`
										width: 174px;
									`}
								>
									<Typography
										variant="div"
										size="sm"
										weight="bold"
										color={theme.colors.primary.header}
									>
										{staticContent.gradeTableHeadings.grade}
									</Typography>
								</TableCell>
								<TableCell
									css={css`
										width: 396px;
									`}
								>
									<Typography
										variant="div"
										size="sm"
										weight="bold"
										color={theme.colors.primary.header}
									>
										{staticContent.gradeTableHeadings.alloyingElement}
									</Typography>
								</TableCell>
								<TableCell
									css={css`
										width: calc(100% - 570px);
									`}
								>
									<Typography
										variant="div"
										size="sm"
										weight="bold"
										color={theme.colors.primary.header}
									>
										{staticContent.gradeTableHeadings.characteristics}
									</Typography>
								</TableCell>
							</TableRow>
							{dynamicContent.gradeTableItems.map(
								(data: GradeTableItem, index: number) => (
									<React.Fragment key={index}>
										<TableRow
											css={css`
												background-color: ${theme.colors.primary
													.background};
												height: auto;
												${dynamicContent.gradeTableItems &&
												dynamicContent.gradeTableItems.length - 1 === index
													? "border-radius: 0 0 2px 2px"
													: "border-radius: 0 0 0 0"};
											`}
										>
											<TableCell
												css={css`
													width: 174px;
												`}
											>
												<Typography
													variant="div"
													size="md"
													color={theme.colors.primary.header}
												>
													{data.grade}
												</Typography>
											</TableCell>
											<TableCell
												css={css`
													width: 396px;
												`}
											>
												<Typography
													variant="div"
													size="md"
													color={theme.colors.primary.header}
												>
													{data.primaryAlloyingElements}
												</Typography>
											</TableCell>
											<TableCell
												css={css`
													width: calc(100% - 570px);
													padding-right: 40px;
												`}
											>
												<Typography
													variant="div"
													size="md"
													color={theme.colors.primary.header}
												>
													{data.characteristics}
												</Typography>
											</TableCell>
											<Divider
												css={css`
													color: ${theme.colors.primary.darkerGray};
												`}
											/>
										</TableRow>
									</React.Fragment>
								)
							)}
						</>
					)}
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="primary" vPadding="40px" />
				<ContentSection type="tertiary" hPadding="20px" vPadding="0">
					<img
						css={css`
							width: calc(100% + 20px);
							margin-left: -20px;
							margin-bottom: 8px;
							margin-top: -40px;
						`}
						src={cards[0].mobileImage}
						alt={cards[0].title}
					/>
					{cards.map((card: IgniteYourCareerCard, index: number) => {
						return (
							<React.Fragment key={index}>
								<Spacer
									css={css`
										height: 20px;
									`}
								/>
								<MobileIcon>
									<Icon color={theme.colors.tertiary.header} icon="check" />
								</MobileIcon>
								<MobileHeading>
									<Typography
										variant="h4"
										type="tertiary"
										weight="bolder"
										css={css`
											font-size: 24px;
											letter-spacing: -1px;
											line-height: 30px;
										`}
									>
										{card.title}
									</Typography>
								</MobileHeading>
								<Typography
									variant="div"
									size="md"
									type="tertiary"
									css={css`
										margin-bottom: 20px;
										margin-top: 14px;
									`}
								>
									{card.description}
								</Typography>
							</React.Fragment>
						);
					})}
					<Spacer />
					<Spacer />
				</ContentSection>
				{dynamicContent.blurb && (
					<ContentSection type="primary" vPadding="32px" hPadding="0px">
						<Typography
							variant="div"
							size="lg"
							type="primary"
							weight="bold"
							css={css`
								letter-spacing: -1px;
							`}
						>
							{dynamicContent.blurb}
						</Typography>
					</ContentSection>
				)}
				{staticContent.gradeTableHeadings && dynamicContent.gradeTableItems && (
					<>
						{dynamicContent.gradeTableItems.map(
							(data: GradeTableItem, index: number) => {
								return (
									<React.Fragment key={index}>
										<ContentSection
											type="secondary"
											vPadding="20px"
											hPadding="18px"
										>
											<Typography
												variant="div"
												size="md"
												weight="bold"
												color={theme.colors.primary.header}
												css={css`
													margin-bottom: 4px;
												`}
											>
												{staticContent.gradeTableHeadings
													? staticContent.gradeTableHeadings.grade
													: ""}
											</Typography>
											<Typography
												variant="div"
												size="md"
												type="secondary"
												css={css`
													margin-bottom: 18px;
												`}
											>
												{data.grade}
											</Typography>
											<Typography
												variant="div"
												size="md"
												weight="bold"
												color={theme.colors.primary.header}
												css={css`
													margin-bottom: 4px;
												`}
											>
												{staticContent.gradeTableHeadings
													? staticContent.gradeTableHeadings
															.alloyingElement
													: ""}
											</Typography>
											<Typography
												variant="div"
												size="md"
												type="secondary"
												css={css`
													margin-bottom: 18px;
												`}
											>
												{data.primaryAlloyingElements}
											</Typography>
											<Typography
												variant="div"
												size="md"
												weight="bold"
												color={theme.colors.primary.header}
												css={css`
													margin-bottom: 4px;
												`}
											>
												{staticContent.gradeTableHeadings
													? staticContent.gradeTableHeadings
															.characteristics
													: ""}
											</Typography>
											<Typography
												variant="div"
												size="md"
												type="secondary"
												css={css`
													margin-bottom: 4px;
												`}
											>
												{data.characteristics}
											</Typography>
										</ContentSection>
										<Spacer
											css={css`
												height: 20px;
											`}
										/>
									</React.Fragment>
								);
							}
						)}
					</>
				)}
				<Spacer
					css={css`
						height: 40px;
					`}
				/>
			</Media>
		</>
	);
};

export default CharacteristicsComponent;
